/**
 * Full-base runner (S-030) — dispara/acompanha/controla a varredura da base
 * ABAP inteira (job durável + scan_worker) via o proxy do api gateway.
 */

import apiClient from "./client";

export interface CustomPackage {
  devclass: string;
  object_count: number;
}

export interface CustomPackagesResponse {
  total_packages: number;
  packages: CustomPackage[];
}

/** Pacotes (DEVCLASS) custom da carga do ex-kit do projeto: alimenta o seletor de escopo da
 * varredura. Projeto sem carga responde 409 (#1785). */
export async function listCustomPackages(
  projectId: string,
): Promise<CustomPackagesResponse> {
  const res = await apiClient.get<CustomPackagesResponse>(
    `/api/projects/${projectId}/custom-packages`,
  );
  return res.data;
}

const AGENT_ID = "architect";

/**
 * Desde a #1785 a varredura é do projeto: lê o código do cliente da carga do ex-kit, sem conexão
 * SAP, e o backend escolhe a origem sozinho.
 */
export interface FullbaseInputs {
  analysis_batch_size?: number;
  parallelism?: number;
  /** Lista de pacotes (devclass) separada por vírgula. Vazio = base inteira. */
  package_filter?: string;
  /**
   * Profundidade da análise (#835). `light` pula o analisador por objeto, que é
   * onde o dinheiro vai; `full` é o comportamento histórico.
   *
   * Medido em 14/09/2026 sobre os 38.639 objetos do IDES: a profunda cota
   * US$ 1.062–1.592 e a leve US$ 77–193. O backend sempre soube ler o campo
   * (`scan_dispatch`, `is_light_pass`); a tela é que não o mandava, e
   * `normalize_depth` trata ausência como `full`.
   */
  analysis_depth?: "light" | "full";
}

export interface FullbaseStartResult {
  status: string;
  project_id: string;
  job_id?: string;
}

/** Status do job durável (do /fullbase/status job-backed). */
export interface FullbaseStatus {
  project_id: string;
  job_id?: string;
  status: string; // queued|estimating|extracting|analyzing|synthesizing|paused|cancelling|cancelled|failed|completed|idle
  phase?: string;
  pause_reason?: string | null;
  total_discovered?: number;
  extracted_programs: number;
  /**
   * O universo de ORIGEM do projeto, sem filtro de pacote (#829).
   *
   * O `extracted_programs` acima é do escopo do job. Comparar o catálogo
   * cumulativo contra ele fazia a cobertura fechar sozinha: 148 analisados de
   * 252 contra os 91 do último job davam "covered".
   */
  source_total?: number;
  /** Quando o universo foi contado. Denominador velho com numerador novo é a
   *  mesma classe de erro que a #829 corrigiu uma vez. */
  source_counted_at?: string | null;
  /**
   * O que a varredura vai de fato processar: o inventário do job (#853).
   *
   * É a população que o `discover` grava e que **não muda depois**, distinta do
   * `total_discovered` (contagem crua da TADIR, que inclui DTEL/TABL/TRAN sem
   * fonte) e do `extracted_programs` (que cresce durante a extração). Medido em
   * 16/09/2026 no IDES: 38.639 contra 11.370.
   *
   * Zero enquanto o `discover` roda — aí não há denominador e a barra é
   * indeterminada.
   */
  inventory_total?: number;
  /** Do inventário, quantos nunca terão fonte para ler. Entram no numerador,
   *  porque estão resolvidos: eram 30,1% naquela varredura, e sem somá-los uma
   *  varredura completa pararia em 70%. */
  inventory_no_source?: number;
  /**
   * #1111: os objetos do inventário que o catálogo já tem resolvidos. É o
   * numerador da barra: o sem fonte só conta depois de a linha dele chegar ao
   * catálogo, e o extraído só no SHA atual.
   */
  inventory_resolved?: number;
  /** Objetos analisados no sha atual (done = extracted − unanalyzed). */
  analyzed: number;
  /** Objetos extraídos que ainda faltam analisar (source novo/mudado por sha). */
  unanalyzed?: number;
  pct?: number;
  /** Quando o último lote de análise commitou (heartbeat da barra). */
  last_batch_at?: string | null;
  parallelism?: number;
  est_cost_usd_min?: number | null;
  est_cost_usd_max?: number | null;
  est_seconds?: number | null;
  actual_cost_usd?: number | null;
  running: boolean;
  error: string | null;
}

export interface FullbaseEstimate {
  objects: number;
  parallelism: number;
  est_cost_usd_min: number;
  est_cost_usd_max: number;
  est_seconds: number;
  calibration_source: string; // "seed" | "measured"
}

const TERMINAL = new Set(["completed", "cancelled", "failed"]);

export async function startFullbase(
  projectId: string,
  inputs: FullbaseInputs,
): Promise<FullbaseStartResult> {
  const res = await apiClient.post<FullbaseStartResult>(
    `/api/projects/${projectId}/agents/${AGENT_ID}/fullbase`,
    { inputs },
  );
  return res.data;
}

/** Resultado do reprocesso: o que é reavaliação e o que é linha nova (#786). */
export interface ReprocessResult extends FullbaseStartResult {
  packages?: string[];
  /** Linhas que já existiam no catálogo e serão reavaliadas. */
  reanalyzed?: number;
  /**
   * Objetos do índice de fontes sem linha no catálogo — o backfill que vem
   * junto. `null` é "não consegui contar", que não é a mesma coisa que zero.
   */
  new_from_index?: number | null;
}

/**
 * Reprocessa IN-PLACE os pacotes escolhidos: reavalia as linhas que já existem e
 * **traz junto** os objetos do pacote que estão no índice de fontes e nunca
 * entraram no catálogo. Não cria scan novo nem entra no histórico; o resto do
 * catálogo fica intacto.
 *
 * O backfill é deliberado (é o que se quer depois de uma varredura parcial), e
 * por isso a resposta separa as duas contagens — reprocessar `Z610` levou o
 * pacote de 24 para 78 linhas, e sem a separação a conta maior chega sem aviso.
 */
export async function reprocessPackages(
  projectId: string,
  packages: string[],
): Promise<ReprocessResult> {
  const res = await apiClient.post<ReprocessResult>(
    `/api/projects/${projectId}/agents/${AGENT_ID}/fullbase/reprocess`,
    { packages },
  );
  return res.data;
}

export async function estimateFullbase(
  projectId: string,
  inputs: FullbaseInputs,
): Promise<FullbaseEstimate> {
  const res = await apiClient.post<FullbaseEstimate>(
    `/api/projects/${projectId}/agents/${AGENT_ID}/fullbase/estimate`,
    { inputs },
  );
  return res.data;
}

export async function getFullbaseStatus(
  projectId: string,
): Promise<FullbaseStatus> {
  const res = await apiClient.get<FullbaseStatus>(
    `/api/projects/${projectId}/agents/${AGENT_ID}/fullbase/status`,
  );
  return res.data;
}

/** Um scan (job) no histórico — 1 por varredura lógica (não por fase). */
export interface FullbaseScan {
  job_id: string;
  status: string;
  package_filter?: string | null;
  /** Conexão SAP do scan original (informativo). */
  connection_id?: string | null;
  /** Escopo TADIR descoberto (inclui DDIC/standard sem source). */
  total_discovered: number;
  /** Objetos custom Z/Y catalogados no escopo do scan — o resultado de fato. */
  cataloged?: number;
  parallelism?: number | null;
  est_cost_usd_min?: number | null;
  est_cost_usd_max?: number | null;
  est_seconds?: number | null;
  actual_cost_usd?: number | null;
  error?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
}

/** Histórico de scans da base (jobs), mais recentes primeiro. */
export async function listFullbaseScans(
  projectId: string,
): Promise<FullbaseScan[]> {
  const res = await apiClient.get<FullbaseScan[]>(
    `/api/projects/${projectId}/agents/${AGENT_ID}/fullbase/jobs`,
  );
  return res.data;
}

async function control(projectId: string, action: "cancel" | "pause" | "resume") {
  const res = await apiClient.post(
    `/api/projects/${projectId}/agents/${AGENT_ID}/fullbase/${action}`,
  );
  return res.data;
}

export const cancelFullbase = (p: string) => control(p, "cancel");
export const pauseFullbase = (p: string) => control(p, "pause");
export const resumeFullbase = (p: string) => control(p, "resume");

/** % de análise sobre o índice (extraído). Usa pct do backend quando vem. */
export function fullbaseProgressPercent(status: FullbaseStatus | undefined): number {
  if (!status) return 0;
  if (typeof status.pct === "number") return Math.max(0, Math.min(100, status.pct));
  // #853: o denominador é o inventário, que não se move. Era
  // `extracted_programs`, que cresce durante a extração — numerador e
  // denominador andavam juntos e a barra media o crescimento do denominador.
  const denom = status.inventory_total ?? 0;
  if (!denom) return 0;
  return Math.max(0, Math.min(100, Math.round((fullbaseProcessed(status) / denom) * 100)));
}

/**
 * O numerador da barra: o que o catálogo já tem resolvido do inventário do job.
 *
 * #853: o rótulo ao lado da barra mostra este número, e não só o `analyzed`;
 * com `0 of 11.370 analyzed` ao lado de uma barra em 3%, quem lê não sabe o que
 * está acompanhando.
 *
 * #1111: era analisados mais sem-fonte do ledger, e o sem-fonte entra no ledger
 * antes de a linha dele chegar ao catálogo. No segundo cliente real a tela disse
 * "622 de 622 · 100%" com 495 linhas no catálogo. O número vem do backend.
 */
export function fullbaseProcessed(status: FullbaseStatus | undefined): number {
  if (!status?.inventory_total) return 0;
  return Math.max(0, Math.min(status.inventory_total, status.inventory_resolved ?? 0));
}

/**
 * Enquanto o `discover` monta o inventário não há denominador (#853).
 *
 * Barra indeterminada é mais honesta que um percentual calculado sobre uma
 * população que ainda está se formando.
 */
export function isFullbaseProgressUnknown(
  status: FullbaseStatus | undefined,
): boolean {
  return !!status && !status.inventory_total;
}

export function isTerminal(status: FullbaseStatus | undefined): boolean {
  return !!status && TERMINAL.has(status.status);
}

const STAGE_LABEL: Record<string, string> = {
  idle: "Ready to start",
  queued: "Queued",
  estimating: "Counting the scope",
  extracting: "Extracting source from the base (RFC)",
  analyzing: "Analyzing objects in batches",
  synthesizing: "Generating report",
  paused: "Paused",
  cancelling: "Cancelling",
  cancelled: "Canceled",
  failed: "Failed",
  completed: "Completed",
};

export function fullbaseStageLabel(status: FullbaseStatus | undefined): string {
  if (!status) return STAGE_LABEL.idle;
  return STAGE_LABEL[status.status] ?? status.status;
}

/** Formata segundos em "~Xh Ym" / "~Y min" / "~Zs". */
export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return "—";
  if (seconds < 60) return `~${Math.round(seconds)}s`;
  if (seconds < 3600) return `~${Math.round(seconds / 60)} min`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return `~${h}h ${m}m`;
}

/** "há ~Xs" / "há ~Y min" desde um ISO timestamp. Heartbeat do último lote. */
export function formatAgo(iso: string | null | undefined, now?: number): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const secs = Math.max(0, Math.round(((now ?? Date.now()) - t) / 1000));
  if (secs < 60) return `há ~${secs}s`;
  return `há ~${Math.round(secs / 60)} min`;
}

export function formatUsdRange(
  min: number | null | undefined,
  max: number | null | undefined,
): string {
  if (min == null || max == null) return "—";
  const f = (v: number) => `US$ ${v.toFixed(2)}`;
  return min === max ? f(min) : `${f(min)} - ${f(max)}`;
}
